import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useML } from '../contexts/MLContext';
import PinModal from './Modal/PinModal';
import TermsModal from './TermsModal';
import { useValueVisibility } from '../contexts/ValueVisibilityContext';
import { WORKER_URL, getAuthHeaders } from '../utils/api';

const REDIRECT_URI = 'https://integra-mkt-front.pages.dev/settings';
const CLIENT_ID = '6754058170889406';

function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMLConnected, setIsMLConnected } = useML();
  const [userData, setUserData] = useState(null);
  const [labelFormat, setLabelFormat] = useState(localStorage.getItem('label_format') || 'zpl');
  const [pinProtectionEnabled, setPinProtectionEnabled] = useState(() => localStorage.getItem('pin_protection') === 'true');
  const [showPinModal, setShowPinModal] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(null);
  const { setShowValues, handleProtectionChange } = useValueVisibility();

  const getUserData = async () => {
    try {
      const res = await fetch(`${WORKER_URL}/api/user/me`, { headers: getAuthHeaders() });
      if (res.ok) setUserData(await res.json());
    } catch {}
  };

  const exchangeCodeForToken = async (code) => {
    setStatusMsg('Conectando...');
    try {
      const res = await fetch(`${WORKER_URL}/api/ml/connect`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: REDIRECT_URI })
      });
      const data = await res.json();
      if (res.ok) {
        setIsMLConnected(true);
        setStatusMsg('');
        getUserData();
        navigate('/settings', { replace: true });
      } else {
        setStatusMsg('Erro: ' + (data.error || 'Falha ao conectar'));
      }
    } catch {
      setStatusMsg('Erro ao conectar com o Mercado Livre');
    }
  };

  const handleConnect = () => {
    const scopes = 'offline_access read_orders write_orders write_ml_account read_listings write_listings';
    window.location.href = `https://auth.mercadolivre.com.br/authorization?response_type=code&client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${encodeURIComponent(scopes)}`;
  };

  const handleDisconnect = () => {
    setIsMLConnected(false);
    setUserData(null);
  };

  useEffect(() => {
    fetch(`${WORKER_URL}/api/user/subscription`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => setTermsAccepted(data?.termsAccepted ?? false))
      .catch(() => setTermsAccepted(false));
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    if (code) { exchangeCodeForToken(code); return; }
    if (isMLConnected) getUserData();
  }, [location.search]);

  if (termsAccepted === false) {
    return <TermsModal onAccepted={() => setTermsAccepted(true)} />;
  }

  return (
    <>
      {showPinModal ? (
        <PinModal
          mode="create"
          onSuccess={() => { setPinProtectionEnabled(true); localStorage.setItem('pin_protection', 'true'); setShowPinModal(false); handleProtectionChange(true); }}
          onClose={() => setShowPinModal(false)}
        />
      ) : (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', gap: '40px' }}>
            {/* Lado Esquerdo - Mercado Livre */}
            <div style={{ flex: 1 }}>
              <h2 style={{ color: '#2D3277', marginBottom: '20px' }}>Configurações do Mercado Livre</h2>
              {statusMsg && <p style={{ color: '#ff8000', marginBottom: '12px' }}>{statusMsg}</p>}
              <div style={{ maxWidth: '400px', marginBottom: '20px' }}>
                {isMLConnected ? (
                  <div>
                    {userData && (
                      <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                        <p style={{ margin: '0 0 6px 0' }}><strong>Usuário:</strong> {userData.nickname}</p>
                        <p style={{ margin: '0 0 6px 0' }}><strong>ID:</strong> {userData.id}</p>
                        <p style={{ margin: 0 }}><strong>Reputação:</strong> {userData.seller_reputation?.level_id || 'N/A'}</p>
                      </div>
                    )}
                    <button onClick={handleDisconnect} style={{ backgroundColor: '#ff4444', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Desconectar
                    </button>
                  </div>
                ) : (
                  <button onClick={handleConnect} style={{ backgroundColor: '#2D3277', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Conectar com Mercado Livre
                  </button>
                )}
              </div>
            </div>

            {/* Lado Direito - Configurações Gerais */}
            <div style={{ flex: 1 }}>
              <h2 style={{ color: '#2D3277', marginBottom: '20px' }}>Configurações Gerais</h2>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '10px' }}>Formato Padrão da Etiqueta</h3>
                <div style={{ display: 'flex', gap: '15px' }}>
                  {['zpl', 'pdf'].map(fmt => (
                    <label key={fmt} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="radio" name="labelFormat" value={fmt} checked={labelFormat === fmt} onChange={() => { setLabelFormat(fmt); localStorage.setItem('label_format', fmt); }} />
                      {fmt === 'zpl' ? 'ZPL (.txt)' : 'PDF'}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '10px' }}>Proteção de Dados</h3>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '4px' }}>
                  <span>Usar PIN para visualizar valores</span>
                  <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                    <input type="checkbox" checked={pinProtectionEnabled}
                      onChange={(e) => {
                        if (e.target.checked) setShowPinModal(true);
                        else { setPinProtectionEnabled(false); localStorage.setItem('pin_protection', 'false'); handleProtectionChange(false); }
                      }}
                      style={{ display: 'none' }}
                    />
                    <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: pinProtectionEnabled ? '#2D3277' : '#ccc', transition: '0.4s', borderRadius: '24px' }}>
                      <div style={{ height: '20px', width: '20px', position: 'absolute', left: '2px', bottom: '2px', backgroundColor: 'white', transition: '0.4s', borderRadius: '50%', transform: `translateX(${pinProtectionEnabled ? '26px' : '2px'})` }} />
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Settings;
